import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Wind, RotateCcw, User, Ruler, Play } from "lucide-react";
import { logInterventionWithToast } from "@/utils/interventionLogger";

const MODES = [
  { id: "scmv", label: "(S)CMV", desc: "Volume-targeted, pressure-controlled" },
  { id: "simv", label: "SIMV", desc: "Synchronized intermittent mandatory" },
  { id: "asv", label: "ASV", desc: "Adaptive support ventilation" },
  { id: "niv", label: "NIV", desc: "Non-invasive ventilation" },
];

// IBW calculation (Devine formula)
function calcIBW(heightCm, gender) {
  const inches = heightCm / 2.54;
  if (gender === "male") return Math.round(50 + 2.3 * (inches - 60));
  return Math.round(45.5 + 2.3 * (inches - 60));
}

export default function VentilatorTool({ defaultPopulation = "adult" }) {
  const [gender, setGender] = useState("male");
  const [heightCm, setHeightCm] = useState("");
  const [population, setPopulation] = useState(defaultPopulation); // adult | peds
  const [mode, setMode] = useState("scmv");
  const [vt, setVt] = useState(""); // mL
  const [rr, setRr] = useState("20");
  const [peep, setPeep] = useState("5");
  const [fio2, setFio2] = useState("40");
  const [ie, setIe] = useState("1:2");
  const [psupport, setPsupport] = useState("10");

  const ibw = useMemo(() => {
    if (!heightCm || parseFloat(heightCm) < 100) return null;
    return calcIBW(parseFloat(heightCm), gender);
  }, [heightCm, gender]);

  // Clamp Vt into the valid range when population switches between adult and peds
  useEffect(() => {
    if (!vt) return;
    const v = parseFloat(vt);
    const min = population === "peds" ? 20 : 200;
    const max = population === "peds" ? 300 : 800;
    if (v < min) setVt(min.toString());
    else if (v > max) setVt(max.toString());
  }, [population]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate Vt from IBW if not manually set
  const suggestedVt = ibw ? Math.round(ibw * 6) : null;
  const suggestedVtHigh = ibw ? Math.round(ibw * 8) : null;
  const activeVt = vt || (suggestedVt ? suggestedVt.toString() : "");
  const minuteVol = activeVt && rr ? ((parseFloat(activeVt) * parseFloat(rr)) / 1000).toFixed(1) : null;

  const handleReset = () => {
    setGender("male"); setHeightCm(""); setPopulation(defaultPopulation);
    setMode("scmv"); setVt(""); setRr("20"); setPeep("5");
    setFio2("40"); setIe("1:2"); setPsupport("10");
  };

  const Param = ({ label, value, unit, color = "text-foreground", large }) => (
    <div className="bg-secondary/30 border border-border rounded-lg p-2.5 text-center">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`font-mono font-bold ${color} ${large ? 'text-2xl' : 'text-lg'}`}>{value || "—"}</div>
      {unit && <div className="text-[10px] text-muted-foreground">{unit}</div>}
    </div>
  );

  const Slider = ({ label, value, onChange, min, max, step, unit, color }) => {
    const nudge = (delta) => {
      const next = Math.min(max, Math.max(min, parseFloat(value) + delta));
      onChange(String(next));
    };
    return (
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium">{label}</span>
          <span className={`text-sm font-mono font-bold ${color || ''}`}>{value} {unit}</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => nudge(-step)} className="h-7 w-7 shrink-0 rounded bg-secondary/40 hover:bg-secondary/70 text-muted-foreground font-bold transition-all" aria-label={`Decrease ${label}`}>−</button>
          <input
            type="range" min={min} max={max} step={step} value={value}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <button type="button" onClick={() => nudge(step)} className="h-7 w-7 shrink-0 rounded bg-secondary/40 hover:bg-secondary/70 text-muted-foreground font-bold transition-all" aria-label={`Increase ${label}`}>+</button>
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground px-9">
          <span>{min}</span><span>{max}</span>
        </div>
      </div>
    );
  };

  return (
    <Card className="border-cyan-500/30" data-testid="ventilator-tool">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base font-heading">
          <span className="flex items-center gap-2">
            <Wind className="w-4 h-4 text-cyan-400" />
            Ventilator Settings
            <Badge variant="outline" className="text-[9px] font-normal">Hamilton T1</Badge>
          </span>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleReset} data-testid="vent-reset">
            <RotateCcw className="w-3 h-3 mr-1" />Reset
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-4">

        {/* Patient Info */}
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <User className="w-3 h-3" /> Patient
          </div>
          <div className="grid grid-cols-3 gap-2">
            {/* Population */}
            <div className="flex gap-1 bg-secondary/20 rounded-lg p-0.5">
              <button onClick={() => setPopulation("adult")} className={`flex-1 py-1.5 rounded text-xs font-bold transition-all ${population === "adult" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Adult</button>
              <button onClick={() => setPopulation("peds")} className={`flex-1 py-1.5 rounded text-xs font-bold transition-all ${population === "peds" ? "bg-violet-600 text-white" : "text-muted-foreground"}`}>Peds</button>
            </div>
            {/* Gender */}
            <div className="flex gap-1 bg-secondary/20 rounded-lg p-0.5">
              <button onClick={() => setGender("male")} className={`flex-1 py-1.5 rounded text-xs font-bold transition-all ${gender === "male" ? "bg-blue-600 text-white" : "text-muted-foreground"}`}>M</button>
              <button onClick={() => setGender("female")} className={`flex-1 py-1.5 rounded text-xs font-bold transition-all ${gender === "female" ? "bg-pink-600 text-white" : "text-muted-foreground"}`}>F</button>
            </div>
            {/* Height */}
            <div className="relative">
              <Ruler className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="number" inputMode="numeric" placeholder="Height cm"
                value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 rounded-lg bg-secondary/30 border border-border text-sm font-mono"
                data-testid="vent-height"
              />
            </div>
          </div>
          {ibw && (
            <div className="text-xs text-cyan-400 font-mono text-center">
              IBW: {ibw} kg | Suggested Vt: {suggestedVt}-{suggestedVtHigh} mL (6-8 mL/kg)
            </div>
          )}
        </div>

        {/* Mode Selection */}
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mode</div>
          <div className="grid grid-cols-4 gap-1.5">
            {MODES.map(m => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${mode === m.id ? "bg-cyan-600 text-white ring-2 ring-cyan-500/50" : "bg-secondary/40 text-muted-foreground hover:bg-secondary/60"}`}
                data-testid={`vent-mode-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground text-center">{MODES.find(m => m.id === mode)?.desc}</div>
        </div>

        {/* Current Settings Display */}
        <div className="grid grid-cols-4 gap-2" data-testid="vent-display">
          <Param label="Vt" value={activeVt} unit="mL" color="text-cyan-400" large />
          <Param label="RR" value={rr} unit="bpm" color="text-green-400" large />
          <Param label="PEEP" value={peep} unit="cmH₂O" color="text-amber-400" large />
          <Param label="FiO₂" value={fio2} unit="%" color="text-blue-400" large />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Param label="I:E" value={ie} color="text-foreground" />
          <Param label="MV" value={minuteVol} unit="L/min" color="text-emerald-400" />
          <Param label="Mode" value={MODES.find(m => m.id === mode)?.label} color="text-cyan-400" />
        </div>

        {/* Parameter Sliders */}
        <div className="space-y-3">
          <Slider label="Tidal Volume (Vt)" value={activeVt || (population === "peds" ? 20 : 200)} onChange={setVt} min={population === "peds" ? 20 : 200} max={population === "peds" ? 300 : 800} step={10} unit="mL" color="text-cyan-400" />
          <Slider label="Respiratory Rate" value={rr} onChange={setRr} min={6} max={40} step={1} unit="bpm" color="text-green-400" />
          <Slider label="PEEP" value={peep} onChange={setPeep} min={0} max={25} step={1} unit="cmH₂O" color="text-amber-400" />
          <Slider label="FiO₂" value={fio2} onChange={setFio2} min={21} max={100} step={1} unit="%" color="text-blue-400" />

          {/* I:E Ratio */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium">I:E Ratio</span>
            </div>
            <div className="flex gap-1.5">
              {["1:1", "1:1.5", "1:2", "1:2.5", "1:3", "1:4"].map(r => (
                <button key={r} onClick={() => setIe(r)} className={`flex-1 py-1.5 rounded text-xs font-mono font-bold transition-all ${ie === r ? "bg-primary text-primary-foreground" : "bg-secondary/40 text-muted-foreground hover:bg-secondary/60"}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          {(mode === "simv" || mode === "niv") && (
            <Slider label="Pressure Support" value={psupport} onChange={setPsupport} min={0} max={30} step={1} unit="cmH₂O" color="text-violet-400" />
          )}
        </div>

        {/* Start Ventilation — log to intervention tracker */}
        <Button
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
          disabled={!activeVt}
          onClick={() => {
            const modeLabel = MODES.find((m) => m.id === mode)?.label || mode;
            const settings = `Vt ${activeVt} mL · RR ${rr} · PEEP ${peep} · FiO₂ ${fio2}% · I:E ${ie}`;
            const ps = (mode === "simv" || mode === "niv") ? ` · PS ${psupport}` : "";
            const ibwPart = ibw ? ` · IBW ${ibw} kg` : "";
            const popPart = population === "peds" ? "Peds" : "Adult";
            logInterventionWithToast(
              {
                type: "ventilation",
                name: `Mechanical ventilation (${modeLabel})`,
                dose: `${settings}${ps}`,
                route: "ETT",
                notes: `${popPart}${heightCm ? ` · Ht ${heightCm} cm` : ""}${ibwPart} · MV ${minuteVol || "—"} L/min`,
              },
              `Ventilation started — ${modeLabel}`
            );
          }}
          data-testid="start-ventilation"
        >
          <Play className="w-4 h-4 mr-2" />
          Start Ventilation &amp; Log
        </Button>

        {/* Quick Reference */}
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1 font-medium">YEMS Initial Vent Settings Reference</summary>
          <div className="mt-1 space-y-1.5 p-2 bg-secondary/20 rounded-lg">
            <div><span className="font-semibold text-cyan-400">Adult Initial:</span> AC/Volume Mode, Vt 6 mL/kg IBW, RR 20, PEEP 5, FiO₂ 40%, IFR 60 L/min</div>
            <div><span className="font-semibold text-violet-400">Peds Initial:</span> PIP 20-30 cmH₂O, FiO₂ goal &lt;0.6, PEEP 3-5 cmH₂O</div>
            <div><span className="font-semibold text-amber-400">Head Trauma:</span> Target EtCO₂ 35-40 mmHg, avoid hyperventilation</div>
            <div><span className="font-semibold text-red-400">ARDS:</span> Vt 4-6 mL/kg IBW, PEEP 8-12, Plateau &lt;30 cmH₂O</div>
          </div>
        </details>

      </CardContent>
    </Card>
  );
}
